import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  Smartphone,
  ChevronRight,
  Loader2,
  ArrowLeft,
  Camera,
  ImageUp,
  Hash,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  visitorAuthService,
  VISITOR_DEMO_PHONE,
  isNeedBadgeVerifyError,
  type MaxContactPayload,
} from "../services/visitorAuthService";
import { visitorSessionService } from "../services/visitorSessionService";
import { claimPendingQr } from "../services/publicQrFlow";
import { useAppStore } from "../store/useAppStore";
import { ApiError, formatApiError } from "../api/client";
import { isApiEnabled } from "../api/config";
import { maxPlatform } from "../services/maxPlatform";
import { debugLog } from "../services/debugLog";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { CodeScanner, type CodeScanResult } from "../components/CodeScanner";
import {
  bindCameraGrantToUser,
  isCameraGrantedForUser,
  markCameraGrantedForUser,
  shouldAutoStartCamera,
} from "../services/cameraPermission";
import {
  isBusyCameraError,
  isCameraApiAvailable,
  isNotAllowedError,
  openCameraStream,
  stopMediaStream,
} from "../services/cameraStream";
import { PhoneInput } from "../components/PhoneInput";
import {
  formatPhoneDisplay,
  inferLockedCountry,
  isCompletePhoneInput,
  normalizePhoneInput,
  phoneDigits,
} from "../utils/phoneFormat";
import { decode1dBarcodeFromImage } from "../services/decodeBarcodeFromImage";
import { PUBLIC_WEB_URL } from "../utils/publicQr";
import {
  EMPTY_LEGAL_CONSENTS,
  LegalConsentCheckboxes,
  legalConsentsReady,
  toLegalConsentsPayload,
  type LegalConsentState,
} from "../components/LegalConsentCheckboxes";

/** Auth method chooser (MAX vs browser) — always on public PWA host. */
const AUTH_CHOOSER_URL = `${PUBLIC_WEB_URL}/go`;
async function completeLogin(
  session: Awaited<ReturnType<typeof visitorAuthService.verifyOtp>>,
  updateUser: ReturnType<typeof useAppStore>["updateUser"],
  selectEvent: ReturnType<typeof useAppStore>["selectEvent"],
  completeOnboarding: ReturnType<typeof useAppStore>["completeOnboarding"],
  navigate: ReturnType<typeof useNavigate>,
) {
  updateUser({
    id: session.user.id,
    fullName: session.user.fullName,
    role: "visitor",
    phone: session.user.phone,
    email: session.user.email,
  });
  try {
    await visitorSessionService.syncProfile(updateUser);
  } catch (err) {
    debugLog.warn("auth", "syncProfile after login failed (non-fatal)", err);
  }
  debugLog.info("auth", "login success", {
    userId: session.user.id,
    events: session.eventAccess?.length,
    primaryEventId: session.primaryEventId,
  });
  bindCameraGrantToUser(session.user.id);

  const claimed = await claimPendingQr();
  if (claimed?.eventId) {
    selectEvent(claimed.eventId);
    completeOnboarding();
    navigate(claimed.navigateTo, { replace: true });
    return;
  }

  // Badge / ticket login must open THAT show — not a stale Урал session in localStorage.
  if (session.primaryEventId) {
    selectEvent(session.primaryEventId);
    completeOnboarding();
    navigate("/home", { replace: true });
    return;
  }

  const next = new URLSearchParams(window.location.search).get("next");
  if (next?.startsWith("/")) {
    navigate(next, { replace: true });
    return;
  }
  navigate("/onboarding", { replace: true });
}

function readTicketFromLocation(): string {
  const params = new URLSearchParams(window.location.search);
  const fromQuery = (params.get("ticket") || params.get("code") || "").trim();
  if (fromQuery) return fromQuery;
  return "";
}

type LoginStep = "phone" | "badge";

export function VisitorLoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { updateUser, selectEvent, completeOnboarding } = useAppStore();
  const isMax = visitorAuthService.isMaxRuntime();
  const isMaxWebApp = visitorAuthService.isMaxWebApp();
  const canSharePhone = visitorAuthService.canSharePhoneInMax();
  const maxOnlyFlow = isMax;
  const [step, setStep] = useState<LoginStep>("phone");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState<string>("7");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingContact, setPendingContact] = useState<MaxContactPayload | undefined>();
  const [scanPaused, setScanPaused] = useState(false);
  const [cameraArmed, setCameraArmed] = useState(false);
  const [cameraStarting, setCameraStarting] = useState(false);
  const [cameraDenied, setCameraDenied] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [badgeStream, setBadgeStream] = useState<MediaStream | null>(null);
  const [ticketCode, setTicketCode] = useState(() => readTicketFromLocation());
  const [decodeHint, setDecodeHint] = useState("");
  const [legalConsents, setLegalConsents] = useState<LegalConsentState>(EMPTY_LEGAL_CONSENTS);
  const [digitsFocused, setDigitsFocused] = useState(false);
  const [keyboardInset, setKeyboardInset] = useState(0);
  const autoTicketRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const digitsInputRef = useRef<HTMLInputElement>(null);
  const digitsBlockRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    debugLog.info("login", "page mount", {
      isMax,
      isMaxWebApp,
      canSharePhone,
      maxOnlyFlow,
      api: isApiEnabled(),
      platform: maxPlatform.getPlatform(),
      initDataLen: maxPlatform.getInitData().length,
      maxUserId: maxPlatform.getUser()?.id,
      hasTicketQuery: Boolean(readTicketFromLocation()),
    });
  }, [isMax, isMaxWebApp, canSharePhone, maxOnlyFlow]);

  useEffect(() => {
    return () => {
      stopMediaStream(badgeStream);
    };
  }, [badgeStream]);

  const scrollDigitsIntoView = () => {
    const run = () => {
      const el = digitsBlockRef.current ?? digitsInputRef.current;
      if (!el) return;
      const scroller = document.querySelector("[data-app-scroll]") as HTMLElement | null;
      if (scroller) {
        const elRect = el.getBoundingClientRect();
        const scRect = scroller.getBoundingClientRect();
        const vvBottom = window.visualViewport
          ? window.visualViewport.offsetTop + window.visualViewport.height
          : window.innerHeight;
        const margin = 24;
        const targetBottom = Math.min(scRect.bottom, vvBottom) - margin;
        if (elRect.bottom > targetBottom || elRect.top < scRect.top + 8) {
          const delta = elRect.bottom - targetBottom;
          scroller.scrollTop += delta;
        }
      } else {
        el.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" });
      }
    };
    requestAnimationFrame(() => {
      run();
      window.setTimeout(run, 80);
      window.setTimeout(run, 280);
      window.setTimeout(run, 450);
    });
  };

  /** iOS Safari: keep digits field above keyboard; avoid 100dvh white gap. */
  useEffect(() => {
    if (step !== "badge") {
      setDigitsFocused(false);
      setKeyboardInset(0);
      return;
    }
    const updateInset = () => {
      maxPlatform.syncVisualViewportCss();
      const vv = window.visualViewport;
      if (!vv) {
        setKeyboardInset(0);
        return;
      }
      const inset = Math.max(0, window.innerHeight - vv.height - (vv.offsetTop || 0));
      setKeyboardInset(inset > 40 ? Math.round(inset) : 0);
    };
    updateInset();
    window.visualViewport?.addEventListener("resize", updateInset);
    window.visualViewport?.addEventListener("scroll", updateInset);
    return () => {
      window.visualViewport?.removeEventListener("resize", updateInset);
      window.visualViewport?.removeEventListener("scroll", updateInset);
    };
  }, [step]);

  useEffect(() => {
    if (step === "badge" && digitsFocused && keyboardInset > 0) {
      scrollDigitsIntoView();
    }
  }, [step, digitsFocused, keyboardInset]);

  const resetBadgeCamera = () => {
    setBadgeStream((prev) => {
      stopMediaStream(prev);
      return null;
    });
    setCameraArmed(false);
    setCameraStarting(false);
    setCameraDenied(false);
    setCameraError("");
  };

  const fillDemo = () => {
    setPhone(phoneDigits(VISITOR_DEMO_PHONE));
    setCountryCode("7");
    setError("");
  };

  const goToBadge = () => {
    setError("");
    setDecodeHint("");
    setScanPaused(false);
    resetBadgeCamera();
    setStep("badge");
  };

  const enableBadgeCamera = (opts?: { fromAuto?: boolean }) => {
    setCameraError("");
    setCameraDenied(false);
    // First await MUST be getUserMedia — keeps user gesture for iOS/Android (tap path).
    void (async () => {
      if (!isCameraApiAvailable()) {
        setCameraError(t("visitor.camera_open_failed"));
        return;
      }
      setCameraStarting(true);
      debugLog.info("camera", opts?.fromAuto ? "login badge auto-start" : "login badge allow tap");
      try {
        const stream = await openCameraStream();
        debugLog.info("camera", "login badge getUserMedia ok", {
          tracks: stream.getVideoTracks().map((tr) => ({
            id: tr.id,
            label: tr.label,
            ready: tr.readyState,
          })),
        });
        // Persist before auth so reopen / next login skips the consent gate.
        markCameraGrantedForUser(null);
        setBadgeStream(stream);
        setCameraArmed(true);
      } catch (err) {
        debugLog.warn("camera", "login badge getUserMedia failed", {
          name: err instanceof Error ? err.name : undefined,
          message: err instanceof Error ? err.message : String(err),
          fromAuto: Boolean(opts?.fromAuto),
        });
        if (isNotAllowedError(err)) {
          setCameraDenied(true);
          setCameraError("");
        } else {
          setCameraError(
            isBusyCameraError(err) ? t("visitor.camera_busy") : t("visitor.camera_open_failed"),
          );
        }
      } finally {
        setCameraStarting(false);
      }
    })();
  };

  // Re-open camera silently when this device already allowed it once.
  useEffect(() => {
    if (step !== "badge") return;
    let cancelled = false;
    void (async () => {
      const auto = await shouldAutoStartCamera(null);
      if (cancelled || !auto) return;
      enableBadgeCamera({ fromAuto: true });
    })();
    return () => {
      cancelled = true;
    };
  }, [step]);

  const handlePhoneNext = () => {
    setError("");
    if (!legalConsentsReady(legalConsents)) {
      setError(t("login.err_consents"));
      return;
    }
    const digits = phoneDigits(phone);
    if (!isCompletePhoneInput(digits, countryCode)) {
      setError(t("login.err_phone"));
      return;
    }
    if (!isApiEnabled()) {
      setError(t("login.err_api"));
      return;
    }
    setPhone(digits);
    setPendingContact(undefined);
    goToBadge();
  };

  const handleMaxSharePhone = async () => {
    setError("");
    if (!legalConsentsReady(legalConsents)) {
      setError(t("login.err_consents"));
      return;
    }
    setLoading(true);
    try {
      const session = await visitorAuthService.loginWithMaxContact(
        toLegalConsentsPayload(legalConsents),
      );
      await completeLogin(session, updateUser, selectEvent, completeOnboarding, navigate);
    } catch (err) {
      debugLog.error("auth", "maxContact failed", {
        name: err instanceof Error ? err.name : typeof err,
        message: err instanceof Error ? err.message : String(err),
        status: err instanceof ApiError ? err.status : undefined,
        code: err instanceof ApiError ? err.code : undefined,
        body: err instanceof ApiError ? err.body : undefined,
        raw: err instanceof Error ? (err as Error & { raw?: unknown }).raw : undefined,
      });
      if (isNeedBadgeVerifyError(err)) {
        setPendingContact(err.contact);
        setPhone(phoneDigits(err.phone));
        setCountryCode(inferLockedCountry(err.phone) || "7");
        setScanPaused(false);
        setStep("badge");
        setError("");
        return;
      }
      if (err instanceof Error && err.message === "max_init_data_missing") {
        setError(t("login.err_open_bot"));
      } else if (err instanceof Error && err.message === "request_contact_unavailable") {
        setError(t("login.err_max_unsupported"));
      } else if (err instanceof Error && err.message === "request_contact_refused") {
        setError(t("login.err_cancelled"));
      } else if (err instanceof Error && err.message === "request_contact_error") {
        setError(t("login.err_max_network"));
      } else if (err instanceof Error && err.message === "request_contact_incomplete") {
        setError(t("login.err_max_incomplete"));
      } else if (err instanceof Error && err.message.includes("request_contact")) {
        setError(t("login.err_max_phone"));
      } else if (err instanceof ApiError && err.code === "invalid_contact_hash") {
        setError(t("login.err_confirm_phone"));
      } else {
        setError(formatApiError(err, t("login.err_max_login")));
      }
    } finally {
      setLoading(false);
    }
  };

  const submitBadgeCode = async (code: string) => {
    // Printed under barcode: "1 506744 004735" → digits only for match
    const trimmed = code.trim().replace(/\s+/g, "");
    if (!trimmed || loading) return;
    if (!legalConsentsReady(legalConsents)) {
      setError(t("login.err_consents"));
      setScanPaused(false);
      return;
    }
    setScanPaused(true);
    setLoading(true);
    setError("");
    setDecodeHint("");
    try {
      debugLog.info("auth", "badge code submit", {
        len: trimmed.length,
        looksUrl: /^https?:\/\//i.test(trimmed) || /cabex\.ru/i.test(trimmed),
        hasContact: Boolean(pendingContact),
        phoneTail: phone ? phone.slice(-4) : undefined,
      });
      const session = await visitorAuthService.loginWithBadge(trimmed, {
        contact: pendingContact,
        phone: pendingContact
          ? undefined
          : normalizePhoneInput(phone, countryCode) || undefined,
        legalConsents: toLegalConsentsPayload(legalConsents),
      });
      await completeLogin(session, updateUser, selectEvent, completeOnboarding, navigate);
    } catch (err) {
      debugLog.error("auth", "badge login failed", err);
      setError(formatApiError(err, t("login.err_badge_login")));
      setScanPaused(false);
    } finally {
      setLoading(false);
    }
  };

  const handleBadgeScan = async (result: CodeScanResult) => {
    if (loading || scanPaused) return;
    await submitBadgeCode(result.text);
  };

  const handleScreenshotUpload = async (file: File | null) => {
    if (!file || loading) return;
    setLoading(true);
    setError("");
    setDecodeHint("");
    setScanPaused(true);
    try {
      const code = await decode1dBarcodeFromImage(file);
      setTicketCode(code);
      debugLog.info("auth", "screenshot 1d decode", { len: code.length });
      await submitBadgeCode(code);
    } catch (err) {
      debugLog.warn("auth", "screenshot decode failed", err);
      setDecodeHint(t("login.screenshot_crop_hint"));
      setError(t("login.err_screenshot_decode"));
      setScanPaused(false);
      setLoading(false);
    }
  };

  // Deep link ?ticket= / ?code= → after phone (or MAX contact) is ready, auto badge-login once.
  useEffect(() => {
    if (step !== "badge" || autoTicketRef.current || loading) return;
    const code = ticketCode.trim();
    if (!code) return;
    const hasPhone =
      Boolean(pendingContact) ||
      isCompletePhoneInput(phoneDigits(phone), countryCode);
    if (!hasPhone && !maxOnlyFlow) return;
    if (maxOnlyFlow && !pendingContact && !phone) return;
    autoTicketRef.current = true;
    void submitBadgeCode(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot deep link
  }, [step, ticketCode, phone, countryCode, pendingContact, maxOnlyFlow]);

  const title = step === "badge" ? t("login.title_badge") : t("login.title");

  if (step === "badge") {
    const collapseCamera = digitsFocused || keyboardInset > 0;
    return (
      <div
        className="min-h-full bg-gradient-to-b from-indigo-50 via-white to-slate-50 flex flex-col"
        style={{
          paddingBottom: keyboardInset > 0 ? keyboardInset + 12 : undefined,
        }}
      >
        <div className="w-full max-w-lg mx-auto flex flex-col flex-1 min-h-0">
          <div className="shrink-0 pt-[max(0.75rem,env(safe-area-inset-top))] px-4 pb-2">
            <div className="flex items-center justify-between gap-3 min-h-9">
              <button
                type="button"
                onClick={() => {
                  resetBadgeCamera();
                  setStep("phone");
                  setError("");
                  setDecodeHint("");
                  setScanPaused(false);
                  setPendingContact(undefined);
                  setDigitsFocused(false);
                  autoTicketRef.current = false;
                }}
                className="inline-flex items-center gap-1.5 -ml-1 px-1 py-1.5 text-[13px] text-gray-500 cursor-pointer hover:text-gray-800 transition-colors"
              >
                <ArrowLeft size={16} /> {t("login.back")}
              </button>
              <div className="flex items-center gap-2">
                <LanguageSwitcher />
              </div>
            </div>
            <h1 className="text-[22px] text-gray-900 mt-2 mb-1 text-center">{title}</h1>
            {!maxOnlyFlow && phone ? (
              <p className="text-[12px] text-gray-500 text-center mb-2">
                {formatPhoneDisplay(phone, countryCode)}
              </p>
            ) : (
              <div className="h-2" />
            )}
          </div>

          <div className="px-4 pb-[max(1rem,env(safe-area-inset-bottom))] flex flex-col gap-3">
            {!collapseCamera ? (
              <div className="relative isolate w-full h-[min(36vh,240px)] rounded-2xl overflow-hidden border border-gray-100 bg-black">
                {!cameraArmed || !badgeStream ? (
                  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 p-5 text-center bg-slate-900">
                    <Camera size={36} className="text-indigo-300" />
                    <p className="text-[15px] text-white font-semibold">
                      {isCameraGrantedForUser(null)
                        ? t("visitor.camera_needed")
                        : t("visitor.camera_access")}
                    </p>
                    <p className="text-[12px] text-white/80 leading-relaxed max-w-xs">
                      {cameraDenied
                        ? t("visitor.camera_settings_hint")
                        : isCameraGrantedForUser(null)
                          ? t("visitor.camera_connecting")
                          : t("visitor.camera_consent")}
                    </p>
                    {cameraError ? <p className="text-[12px] text-red-300">{cameraError}</p> : null}
                    <button
                      type="button"
                      onClick={() => enableBadgeCamera()}
                      disabled={cameraStarting}
                      className="w-full max-w-xs rounded-xl bg-indigo-500 px-4 py-3 text-[14px] font-semibold text-white disabled:opacity-60 active:scale-[0.98] transition-transform"
                    >
                      {cameraStarting
                        ? t("visitor.camera_requesting")
                        : cameraDenied || cameraError
                          ? t("scan.retry")
                          : isCameraGrantedForUser(null)
                            ? t("visitor.camera_needed")
                            : t("scan.allow_camera")}
                    </button>
                  </div>
                ) : (
                  <CodeScanner
                    active
                    variant="badge"
                    initialStream={badgeStream}
                    paused={scanPaused || loading}
                    onScan={handleBadgeScan}
                    className="absolute inset-0 z-0 !h-full !w-full !min-h-full !rounded-none"
                  />
                )}
              </div>
            ) : null}
            {loading && (
              <p className="text-[13px] text-gray-500 text-center flex items-center justify-center gap-2 shrink-0">
                <Loader2 size={16} className="animate-spin" /> {t("login.checking_badge")}
              </p>
            )}
            {error && <p className="text-[13px] text-red-600 text-center shrink-0">{error}</p>}
            {(scanPaused || error || decodeHint) && !loading && (
              <div className="flex flex-col gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setScanPaused(false);
                    setError("");
                    setDecodeHint("");
                    setTicketCode("");
                    autoTicketRef.current = false;
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  className="w-full text-[13px] text-indigo-600 py-1"
                >
                  {t("login.scan_again")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    resetBadgeCamera();
                    setStep("phone");
                    setError("");
                    setDecodeHint("");
                    setScanPaused(false);
                    setTicketCode("");
                    setPendingContact(undefined);
                    setDigitsFocused(false);
                    autoTicketRef.current = false;
                  }}
                  className="w-full flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-[14px] text-gray-700 active:scale-[0.99]"
                >
                  <ArrowLeft size={16} /> {t("login.back")}
                </button>
              </div>
            )}

            {!collapseCamera ? (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-3">
                <p className="flex items-center gap-2 text-[13px] text-gray-700 font-medium">
                  <ImageUp size={16} className="text-indigo-500 shrink-0" />
                  {t("login.upload_screenshot_label")}
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null;
                    e.target.value = "";
                    void handleScreenshotUpload(file);
                  }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-60 text-indigo-700 text-[14px] font-medium transition-colors"
                >
                  {t("login.upload_screenshot")}
                </button>
                {decodeHint ? (
                  <p className="text-[12px] text-amber-800 bg-amber-50 rounded-xl px-3 py-2">{decodeHint}</p>
                ) : null}
              </div>
            ) : null}

            <div
              ref={digitsBlockRef}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-3"
            >
              <p className="flex items-center gap-2 text-[13px] text-gray-700 font-medium">
                <Hash size={16} className="text-indigo-500 shrink-0" />
                {t("login.enter_digits_label")}
              </p>
              <input
                ref={digitsInputRef}
                type="text"
                inputMode="numeric"
                autoComplete="off"
                enterKeyHint="done"
                value={ticketCode}
                onChange={(e) => {
                  setTicketCode(e.target.value);
                  setError("");
                  setDecodeHint("");
                }}
                onFocus={() => {
                  setDigitsFocused(true);
                  scrollDigitsIntoView();
                }}
                onBlur={() => {
                  // Delay so tap on submit still counts before collapse.
                  window.setTimeout(() => setDigitsFocused(false), 180);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void submitBadgeCode(ticketCode);
                }}
                placeholder={t("login.enter_digits_placeholder")}
                disabled={loading}
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-[15px] tracking-wide text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400"
              />
              <button
                type="button"
                onClick={() => void submitBadgeCode(ticketCode)}
                disabled={loading || !ticketCode.trim()}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-[15px] transition-colors"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : t("login.submit_digits")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 via-white to-slate-50 flex flex-col">
      <div className="w-full max-w-lg mx-auto flex flex-col flex-1 min-h-0">
      <div className="pt-[max(1rem,env(safe-area-inset-top))] px-4">
        <div className="flex items-center justify-between gap-3 mb-5 min-h-9">
          {/*
            MAX: native Back/close does not return to the chooser — open public /go
            in the system browser (same UX as picking auth method again).
            Browser: in-app navigate to /go.
          */}
          <a
            href={AUTH_CHOOSER_URL}
            onClick={(e) => {
              if (maxOnlyFlow) {
                e.preventDefault();
                maxPlatform.openExternalLink(AUTH_CHOOSER_URL);
                return;
              }
              e.preventDefault();
              navigate("/go");
            }}
            className="inline-flex items-center gap-1.5 -ml-1 px-1 py-1.5 text-[13px] text-gray-500 cursor-pointer hover:text-gray-800 transition-colors no-underline"
          >
            <ArrowLeft size={16} /> {t("login.back")}
          </a>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
          </div>
        </div>
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mb-4">
          <Smartphone size={20} className="text-white" />
        </div>
        <h1 className="text-[24px] text-gray-900 mb-1">{title}</h1>
        <p className="text-[14px] text-gray-500">
          {maxOnlyFlow ? t("login.subtitle_max") : t("login.subtitle_phone")}
        </p>
        {ticketCode ? (
          <p className="text-[12px] text-indigo-700 bg-indigo-50 rounded-xl px-3 py-2 mt-3">
            {t("login.ticket_pending_hint")}
          </p>
        ) : null}
      </div>

      <div className="flex-1 px-4 pt-6 pb-8">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
          {maxOnlyFlow ? (
            <>
              {!isMaxWebApp && (
                <p className="text-[13px] text-amber-800 bg-amber-50 rounded-xl px-3 py-2">
                  {t("login.open_via_bot")}
                </p>
              )}
              {!canSharePhone && isMaxWebApp && (
                <p className="text-[12px] text-gray-500 text-center">
                  {t("login.share_unavailable")}
                </p>
              )}
              <LegalConsentCheckboxes
                value={legalConsents}
                onChange={setLegalConsents}
                compact
              />
              <button
                type="button"
                onClick={handleMaxSharePhone}
                disabled={loading || !canSharePhone || !legalConsentsReady(legalConsents)}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:opacity-60 text-white text-[15px] transition-colors"
              >
                {loading ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <>
                    {t("login.continue_max")} <ChevronRight size={18} />
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <div>
                <label className="text-[12px] text-gray-500 mb-1.5 block">{t("login.phone_label")}</label>
                <PhoneInput
                  value={phone}
                  countryCode={countryCode}
                  onCountryChange={setCountryCode}
                  onChange={(v) => {
                    setPhone(v);
                    setError("");
                  }}
                  className="w-full"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handlePhoneNext();
                  }}
                />
                <p className="text-[11px] text-gray-400 mt-1.5 leading-snug">{t("login.phone_hint")}</p>
              </div>
              <LegalConsentCheckboxes
                value={legalConsents}
                onChange={setLegalConsents}
                compact
              />
              {import.meta.env.DEV && (
                <button
                  type="button"
                  onClick={fillDemo}
                  className="text-[12px] text-indigo-600 hover:text-indigo-800"
                >
                  {t("login.demo_phone")}
                </button>
              )}
              <button
                type="button"
                onClick={handlePhoneNext}
                disabled={loading || !legalConsentsReady(legalConsents)}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-[15px] transition-colors"
              >
                {t("login.next")} <ChevronRight size={18} />
              </button>
            </>
          )}

          {error && <p className="text-[13px] text-red-600 text-center">{error}</p>}
        </div>
      </div>
      </div>
    </div>
  );
}
